import { getApiErrorMessage } from './apiErrorUtils';
import { errorLogger } from './loggerInstance';
import {
  DEFAULT_USER_FACING_ERROR_MESSAGE,
  toUserFacingErrorMessage,
} from './userFacingErrorMessage';

// Custom error classes for specific error types

export class AppError extends Error {
  constructor(
    message: string,
    public originalError?: unknown,
  ) {
    super(message);
    this.name = 'AppError';

    // Maintain proper stack trace in modern browsers
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

export class AuthError extends AppError {
  constructor(
    message: string,
    public originalError?: unknown,
  ) {
    super(message, originalError);
    this.name = 'AuthError';
  }
}

export class NetworkError extends AppError {
  constructor(
    message: string,
    public originalError?: unknown,
  ) {
    super(message, originalError);
    this.name = 'NetworkError';
  }
}

export class ValidationError extends AppError {
  constructor(
    message: string,
    public field?: string,
    public originalError?: unknown,
  ) {
    super(message, originalError);
    this.name = 'ValidationError';
    this.field = field;
  }
}

export class AlreadySentError extends AppError {
  constructor(
    message: string = 'Message has already been sent',
    public originalError?: unknown,
  ) {
    super(message, originalError);
    this.name = 'AlreadySentError';
  }
}

export class ApiError extends AppError {
  constructor(
    message: string,
    public statusCode?: number,
    public apiMessage?: string,
    public originalError?: unknown,
  ) {
    super(message, originalError);
    this.name = 'ApiError';
  }
}

/**
 * Read `.code` from an arbitrary thrown value (e.g. FirebaseError) using `in` + Reflect.get — no `any`.
 * Returns a display-safe string, or '' if missing or not a string/number.
 */
export function readErrorCode(error: unknown): string {
  if (error === null || typeof error !== 'object' || !('code' in error)) {
    return '';
  }
  const raw: unknown = Reflect.get(error, 'code');
  if (typeof raw === 'string' && raw.trim() !== '') {
    return raw.trim();
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return '';
}

type MaybeAxiosLikeResponse = {
  status?: number;
  statusText?: string;
  data?: { message?: string };
};

type ErrorWithOptionalAxiosShape = Error & {
  response?: MaybeAxiosLikeResponse | null | undefined;
  request?: unknown;
};

/**
 * Type guard to check if an error is an Axios error
 * @param error - The error to check
 * @returns True if the error is an Axios error
 */
export const isAxiosError = (error: unknown): error is import('axios').AxiosError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as { isAxiosError?: boolean }).isAxiosError === true
  );
};

/**
 * Type guard to check if an error has a response property
 * @param error - The error to check
 * @returns True if the error has a response property
 */
export const hasResponse = (
  error: unknown,
): error is { response: { status?: number; data?: unknown } } => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: unknown }).response === 'object' &&
    (error as { response: unknown }).response !== null
  );
};

function isErrorWithResponseKey(error: unknown): error is ErrorWithOptionalAxiosShape {
  return error instanceof Error && 'response' in error;
}

function sanitizeAndRecloneAppError(error: AppError): AppError {
  const msg = toUserFacingErrorMessage(error.message, DEFAULT_USER_FACING_ERROR_MESSAGE);
  if (msg === error.message) return error;
  if (error instanceof ApiError) {
    return new ApiError(msg, error.statusCode, error.apiMessage, error.originalError);
  }
  if (error instanceof AuthError) {
    return new AuthError(msg, error.originalError);
  }
  if (error instanceof NetworkError) {
    return new NetworkError(msg, error.originalError);
  }
  if (error instanceof ValidationError) {
    return new ValidationError(msg, error.field, error.originalError);
  }
  if (error instanceof AlreadySentError) {
    return new AlreadySentError(msg, error.originalError);
  }
  return new AppError(msg, error.originalError);
}

function tryPreserveMfaVerificationErrorObject(error: unknown): AppError | null {
  if (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    (error as { name?: unknown }).name === 'MFAVerificationError'
  ) {
    return error as AppError;
  }
  return null;
}

function tryAxiosTimeoutAsNetworkError(error: unknown): AppError | null {
  if (!isAxiosError(error)) return null;
  const noResponse = error.response === undefined;
  const isTimeout =
    error.code === 'ECONNABORTED' ||
    error.code === 'ETIMEDOUT' ||
    error.code === 'TIMEOUT' ||
    (typeof error.message === 'string' && /timeout.*exceeded/i.test(error.message));
  if (!isTimeout || !noResponse) return null;
  return new NetworkError(
    'The server took too long to respond. Check your connection, or try again in a few moments.',
    error,
  );
}

function tryHandleApiOrNetworkFromResponseError(error: unknown): AppError | null {
  if (!isErrorWithResponseKey(error)) return null;
  const { response, request } = error;
  if (response) {
    const data = response.data;
    const detailMsg =
      typeof data === 'object' && data !== null ? getApiErrorMessage(data) : undefined;
    const rawMessage = detailMsg ?? response.statusText ?? 'API error';
    const message = toUserFacingErrorMessage(rawMessage, DEFAULT_USER_FACING_ERROR_MESSAGE);

    if (response.status === 401 || response.status === 403) {
      return new AuthError(message || 'Authentication error', error);
    }
    return new ApiError(
      message || 'API error',
      response.status,
      typeof data?.message === 'string' ? data.message : undefined,
      error,
    );
  }
  if (request && !response) {
    return new NetworkError('Network error: Unable to reach server', error);
  }
  return null;
}

function attachMfaMetadata(target: AppError, source: Error): void {
  const withMfa = target as AppError & {
    requires_mfa?: boolean;
    mfa_token?: unknown;
    userEmail?: unknown;
  };
  withMfa.requires_mfa = true;
  withMfa.mfa_token = Reflect.get(source, 'mfaToken') ?? Reflect.get(source, 'mfa_token');
  withMfa.userEmail = Reflect.get(source, 'userEmail');
}

function appErrorFromMfaVerificationError(error: Error): AppError {
  const mfaError = new AppError(error.message, error);
  attachMfaMetadata(mfaError, error);
  return mfaError;
}

function appErrorFromLegacyMfaError(error: Error): AppError {
  const mfaError = new AppError(
    toUserFacingErrorMessage(error.message, DEFAULT_USER_FACING_ERROR_MESSAGE),
    error,
  );
  attachMfaMetadata(mfaError, error);
  return mfaError;
}

function transformPlainError(error: Error): AppError {
  if (error.name === 'MFAVerificationError') {
    return appErrorFromMfaVerificationError(error);
  }
  if (Reflect.get(error, 'requires_mfa')) {
    return appErrorFromLegacyMfaError(error);
  }
  if (error.message?.toLowerCase().includes('already sent')) {
    return new AlreadySentError(
      toUserFacingErrorMessage(error.message, DEFAULT_USER_FACING_ERROR_MESSAGE),
      error,
    );
  }
  return new AppError(
    toUserFacingErrorMessage(error.message, DEFAULT_USER_FACING_ERROR_MESSAGE),
    error,
  );
}

function appErrorFromUnknown(error: unknown): AppError {
  const errorMessage = typeof error === 'string' ? error : 'An unknown error occurred';
  return new AppError(
    toUserFacingErrorMessage(errorMessage, DEFAULT_USER_FACING_ERROR_MESSAGE),
    error,
  );
}

export const handleError = (error: unknown): AppError => {
  if (error instanceof AppError) {
    return sanitizeAndRecloneAppError(error);
  }

  const mfaShape = tryPreserveMfaVerificationErrorObject(error);
  if (mfaShape) return mfaShape;

  const timeoutNetwork = tryAxiosTimeoutAsNetworkError(error);
  if (timeoutNetwork) return timeoutNetwork;

  const fromResponse = tryHandleApiOrNetworkFromResponseError(error);
  if (fromResponse) return fromResponse;

  if (error instanceof Error) {
    return transformPlainError(error);
  }

  return appErrorFromUnknown(error);
};

/**
 * Checks if an error is an MFA (Multi-Factor Authentication) error
 * Looks for requires_mfa and mfa_token properties both directly and in originalError
 */
export const isMFAError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const err = error as any;

  // Check if it's an MFAVerificationError instance
  if (err.name === 'MFAVerificationError' || err.constructor?.name === 'MFAVerificationError') {
    return true;
  }

  // Check direct properties (legacy support)
  if (err.requires_mfa && err.mfa_token) return true;

  // Check originalError if error was wrapped
  if (err.originalError && typeof err.originalError === 'object') {
    const orig = err.originalError as any;
    if (orig.name === 'MFAVerificationError' || orig.constructor?.name === 'MFAVerificationError') {
      return true;
    }
    if (orig.requires_mfa && orig.mfa_token) return true;
  }

  return false;
};

/**
 * Extracts MFA token from an MFA error, checking both direct properties and originalError
 */
export const getMFATokenFromError = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') return null;

  const err = error as any;

  // Check MFAVerificationError properties first
  if (err.mfaToken) return err.mfaToken;

  // Check direct properties (legacy support)
  if (err.mfa_token) return err.mfa_token;

  // Check originalError if error was wrapped
  if (err.originalError && typeof err.originalError === 'object') {
    const orig = err.originalError as any;
    if (orig.mfaToken) return orig.mfaToken;
    if (orig.mfa_token) return orig.mfa_token;
  }

  return null;
};

// Log error utility function
export const logError = (error: unknown, context?: string): AppError => {
  const appError = handleError(error);

  // Define the base context type
  type LogContext = {
    error: {
      name: string;
      message: string;
      stack?: string;
      originalError?: unknown;
    };
    context?: string;
    field?: string;
    statusCode?: number;
    apiMessage?: string;
  };

  // Create a base context with common error information
  const logContext: LogContext = {
    error: {
      name: appError.name,
      message: appError.message,
      stack: appError.stack,
      originalError: appError.originalError,
    },
    ...(context ? { context } : {}),
  };

  if (appError instanceof NetworkError) {
    errorLogger.error(`Network error: ${appError.message}`, logContext);
  } else if (appError instanceof AuthError) {
    errorLogger.error(`Authentication error: ${appError.message}`, logContext);
  } else if (appError instanceof ValidationError) {
    errorLogger.warn(`Validation error: ${appError.message}`, {
      ...logContext,
      field: appError.field,
    });
  } else if (appError instanceof AlreadySentError) {
    errorLogger.warn(`Already sent error: ${appError.message}`, logContext);
  } else if (appError instanceof ApiError) {
    errorLogger.error(`API error (${appError.statusCode}): ${appError.message}`, {
      ...logContext,
      statusCode: appError.statusCode,
      apiMessage: appError.apiMessage,
    });
  } else {
    errorLogger.error(`Application error: ${appError.message}`, logContext);
  }

  return appError;
};

// Utility for throwing appropriate error types from API responses
export const throwApiError = (error: Error | unknown, customMessage?: string) => {
  let statusCode: number | undefined;
  let statusText: string | undefined;
  let message: string;

  // Handle axios error objects using type guard
  if (hasResponse(error)) {
    statusCode = error.response.status;
    const resp = error.response as { status?: number; statusText?: string; data?: unknown };
    statusText = resp.statusText;
    const data = resp.data;
    const dataObj =
      data !== undefined && data !== null && typeof data === 'object'
        ? (data as { detail?: unknown; message?: unknown })
        : undefined;
    const detailStr = typeof dataObj?.detail === 'string' ? dataObj.detail : undefined;
    const dataMessageStr = typeof dataObj?.message === 'string' ? dataObj.message : undefined;
    message =
      customMessage || detailStr || dataMessageStr || `API Error ${statusCode}: ${statusText}`;
  } else if (error && typeof error === 'object' && 'request' in error) {
    // Network error - no response received
    message = customMessage || 'Network error: Unable to reach server';
  } else {
    // Other error
    const errorMessage = error instanceof Error ? error.message : undefined;
    message = customMessage || errorMessage || 'Unknown API error';
  }

  message = toUserFacingErrorMessage(message, DEFAULT_USER_FACING_ERROR_MESSAGE);

  if (statusCode === 401 || statusCode === 403) {
    throw new AuthError(message);
  } else {
    throw new ApiError(message, statusCode);
  }
};

/**
 * Helper to extract HTTP status from an error object
 */
export const extractHttpStatus = (error: unknown): number | undefined => {
  if (hasResponse(error)) {
    return error.response.status;
  }
  return undefined;
};
